import { FormEvent, useState } from "react";
import type { AiBusinessProfile } from "@gigflow/shared";
import { apiRequest } from "../api";
import { useAuth } from "../auth";

type ChatItem = { role: "user" | "assistant"; content: string };

export function AiAssistantPage() {
  const { businessId, refreshBusinesses } = useAuth();
  const [message, setMessage] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [messageId, setMessageId] = useState<string | undefined>();
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [preview, setPreview] = useState<AiBusinessProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!businessId || !message.trim()) return;
    const userText = message.trim();
    setPending(true);
    setError(null);
    setChat((prev) => [...prev, { role: "user", content: userText }]);
    setMessage("");
    try {
      const result = await apiRequest<{
        conversationId: string;
        message: { id: string; content: string };
        preview?: AiBusinessProfile;
        profile?: AiBusinessProfile;
      }>(`/businesses/${businessId}/ai/assistant/chat`, {
        method: "POST",
        body: JSON.stringify({
          conversationId,
          message: userText,
          previewOnly: true,
        }),
      });
      setConversationId(result.conversationId);
      setMessageId(result.message.id);
      setPreview(result.preview ?? result.profile ?? null);
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: result.message.content },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assistant failed");
    } finally {
      setPending(false);
    }
  }

  async function apply() {
    if (!businessId || !conversationId || !messageId) return;
    setPending(true);
    setError(null);
    try {
      await apiRequest(`/businesses/${businessId}/ai/assistant/apply`, {
        method: "POST",
        body: JSON.stringify({ conversationId, messageId }),
      });
      await refreshBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Apply failed");
    } finally {
      setPending(false);
    }
  }

  if (!businessId)
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">AI Assistant</p>
          <h1>Update your business with AI</h1>
        </div>
      </header>

      <section>
        <div className="chat-layout">
          <div>
            <div className="chat-log">
              {chat.length === 0 ? (
                <p className="notice">
                  Ask to rewrite your about section, add a service, or adjust
                  your brand tone.
                </p>
              ) : null}
              {chat.map((item, index) => (
                <div key={index} className={`chat-bubble ${item.role}`}>
                  {item.content}
                </div>
              ))}
            </div>
            <form
              className="form-grid"
              onSubmit={send}
              style={{ marginTop: "1rem" }}
            >
              <label>
                Message
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={pending}
              >
                {pending ? "Working..." : "Preview changes"}
              </button>
            </form>
          </div>
          <div>
            <h2>Preview</h2>
            <div className="preview-box">
              {preview ? JSON.stringify(preview, null, 2) : "No preview yet."}
            </div>
            <div className="cta-row" style={{ marginTop: "1rem" }}>
              <button
                className="btn btn-primary"
                type="button"
                disabled={!messageId || pending}
                onClick={() => void apply()}
              >
                Apply preview
              </button>
            </div>
          </div>
        </div>
        {error ? <p className="notice error">{error}</p> : null}
      </section>
    </>
  );
}
